// String unions as `as const` tuples: usable as zod enums, TypeScript types and runtime lists.

export const ROLES = ["employee", "dispatcher", "admin"] as const;
export type Role = (typeof ROLES)[number];

export const TIME_ENTRY_STATUSES = [
  "draft",
  "submitted",
  "approved",
  "rejected",
  "synced",
  "sync_error",
] as const;
export type TimeEntryStatus = (typeof TIME_ENTRY_STATUSES)[number];

// `azk` (extension 2): "AZK abbauen", time off against the working-time account
// (Freizeitausgleich); requested and decided like vacation.
export const ABSENCE_KINDS = ["vacation", "sick", "azk"] as const;
export type AbsenceKind = (typeof ABSENCE_KINDS)[number];

// The kinds an employee requests and a dispatcher decides (date range, workdays, approval).
export const LEAVE_KINDS = ["vacation", "azk"] as const;
export type LeaveKind = (typeof LEAVE_KINDS)[number];

export function isLeaveKind(kind: AbsenceKind): kind is LeaveKind {
  return (LEAVE_KINDS as readonly string[]).includes(kind);
}

export const ABSENCE_STATUSES = [
  "submitted",
  "approved",
  "rejected",
  "withdrawn",
  "synced",
  "sync_error",
] as const;
export type AbsenceStatus = (typeof ABSENCE_STATUSES)[number];

export const SYNC_JOB_KINDS = [
  "bulk_bookings",
  "absence_create",
  "absence_delete",
  "booking_update",
  "booking_delete",
] as const;
export type SyncJobKind = (typeof SYNC_JOB_KINDS)[number];

export const SYNC_JOB_STATUSES = ["queued", "running", "done", "failed"] as const;
export type SyncJobStatus = (typeof SYNC_JOB_STATUSES)[number];

export const SYNC_RUN_KINDS = [
  "master_data",
  "assignments",
  "full",
  "outbound",
  "readback",
  "retention",
] as const;
export type SyncRunKind = (typeof SYNC_RUN_KINDS)[number];

export const SYNC_RUN_TRIGGERS = ["schedule", "manual"] as const;
export type SyncRunTrigger = (typeof SYNC_RUN_TRIGGERS)[number];

// `skipped`: the scheduler did not start the run (call budget, extension 1).
export const SYNC_RUN_STATUSES = ["running", "ok", "error", "skipped"] as const;
export type SyncRunStatus = (typeof SYNC_RUN_STATUSES)[number];

export const SYNC_RUN_SKIP_REASONS = ["budget"] as const;
export type SyncRunSkipReason = (typeof SYNC_RUN_SKIP_REASONS)[number];

// How often a sync kind runs (`settings.schedule_<kind>`, extension 1); `quarter_hourly` is
// the 15-minute cadence of the outbound queue and the read-back.
export const SCHEDULE_FREQUENCIES = [
  "off",
  "quarter_hourly",
  "hourly",
  "daily",
  "weekly",
  "monthly",
] as const;
export type ScheduleFrequency = (typeof SCHEDULE_FREQUENCIES)[number];

export const WARNING_CODES = [
  "break_too_short",
  "shift_too_long",
  "absence_conflict",
  "outside_assignment_hours",
  "assignment_changed",
] as const;
export type WarningCode = (typeof WARNING_CODES)[number];

export const NOTIFICATION_KINDS = [
  "entry_submitted",
  "entry_decided",
  "absence_submitted",
  "absence_decided",
  "sick_reported",
  "sync_failed",
  "assignment_changed",
  "budget_exhausted",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const AUTH_TOKEN_PURPOSES = ["magic_link", "session", "sms_code"] as const;
export type AuthTokenPurpose = (typeof AUTH_TOKEN_PURPOSES)[number];

export const AUDIT_ENTITIES = ["time_entry", "absence_request", "user", "settings"] as const;
export type AuditEntity = (typeof AUDIT_ENTITIES)[number];

export const EMPLOYEE_STATUSES = [
  "Employee",
  "FormerEmployee",
  "DormantEmployeeWithoutPayroll",
  "DormantEmployeeWithPayroll",
] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const ZVOOVE_MODES = ["mock", "real"] as const;
export type ZvooveMode = (typeof ZVOOVE_MODES)[number];

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];
